import { assign, createMachine } from "xstate";
import { initFromInvestmentTypeAction } from "@features/transactionWizard/stateMachine/actions/initFromInvestmentTypeAction";
import { addAxiosErrorToContextAction } from "./actions/addAxiosErrorToContextAction";
import { addBookingValuesAction } from "./actions/addBookingValuesAction";
import { addValidationErrorAction } from "./actions/addValidationErrorAction";
import { clearValidationErrorAction } from "./actions/clearValidationErrorAction";
import { setBookingTypeAction } from "./actions/setBookingTypeAction";
import { setDepotAction } from "./actions/setDepotAction";
import { setNameAndDepotAction } from "./actions/setNameAndDepotAction";
import { setSelectableDepotsAction } from "./actions/setSelectableDepotsAction";
import { setSelectedCurrencyAction } from "./actions/setSelectedCurrencyAction";
import { setSymbolAction } from "./actions/setSymbolAction";
import { loadDepots } from "./services/loadDepots";
import { persistBooking } from "./services/persistBooking";
import { persistDepot } from "./services/persistDepot";
import { validateBooking } from "./services/validateBooking";
import { TransactionWizardState } from "./types/transactionWizardState";
import {
  defaultContext,
  TransactionWizardContext,
} from "./types/transactionWizardContext";
import { TransactionWizardEvent } from "./types/TransactionWizardEvent";
import { loadSymbol } from "./services/loadSymbol";
import { setEntryQuoteAction } from "./actions/setEntryQuoteAction";

export const newTransactionWizardSM = createMachine<
  TransactionWizardContext,
  TransactionWizardEvent,
  TransactionWizardState
>(
  {
    id: "newTransactionWizardSM",

    // Initial state
    initial: "idle",

    // Local context for entire machine
    context: defaultContext,

    // State definitions
    states: {
      idle: {
        on: {
          INIT_FROM_SYMBOL_SEARCH: {
            target: "selectInvestmentType",
            actions: ["setDepotAction"],
          },
          INIT_FROM_TICKER: {
            target: "loadSymbol",
            actions: ["setDepotAction"],
          },
        },
      },
      selectInvestmentType: {
        on: {
          INVESTMENT_TYPE_SELECTED: [
            {
              target: "selectBookingType",
              actions: ["initFromInvestmentTypeAction"],
              cond: (_, event) =>
                event.investmentType === "71_massets" ||
                event.investmentType === "91_managed",
            },
            {
              target: "searchSymbol",
              actions: ["initFromInvestmentTypeAction"],
            },
          ],
        },
      },
      searchSymbol: {
        on: {
          INIT_FROM_SYMBOL: { target: "selectBookingType" },
          BACK: [
            {
              target: "idle",
            },
          ],
        },
        exit: ["setSymbolAction"],
      },
      loadSymbol: {
        invoke: {
          id: "loadSymbol",
          src: loadSymbol,
        },
        on: {
          INIT_FROM_SYMBOL: {
            target: "selectBookingType",
            actions: ["setSymbolAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      selectBookingType: {
        id: "selectBookingType",
        on: {
          BOOKING_TYPE_SELECTED: {
            target: "depots",
            actions: ["setBookingTypeAction"],
          },
          BACK: [
            {
              target: "selectInvestmentType",
              cond: (context) =>
                context.investmentValues.type === "71_massets" ||
                context.investmentValues.type === "91_managed",
            },
            {
              target: "searchSymbol",
            },
          ],
        },
      },
      depots: {
        initial: "loadDepots",
        states: {
          loadDepots: {
            invoke: {
              id: "loadDepots",
              src: loadDepots,
            },
            on: {
              DEPOTS_LOADED: { target: "selectDepot" },
              SOMETHING_WRONG: { target: "#somethingWrong" },
            },
          },
          selectDepot: {
            entry: ["setSelectableDepotsAction"],
            on: {
              NAME_AND_DEPOT_SELECTED: {
                target: "#priceAndDate",
                actions: ["setNameAndDepotAction"],
              },
              CREATE_NEW_DEPOT: {
                target: "createNewDepot",
                actions: ["setNameAndDepotAction"],
              },
              BACK: { target: "#selectBookingType" },
            },
          },
          createNewDepot: {
            on: {
              PERSIST_DEPOT: { target: "persistNewDepot" },
              BACK: { target: "selectDepot" },
            },
          },
          persistNewDepot: {
            invoke: {
              id: "persistDepot",
              src: persistDepot,
            },
            on: {
              DEPOT_PERSISTED: { target: "loadDepots" },
            },
            exit: ["setDepotAction"],
          },
        },
      },
      priceAndDate: {
        id: "priceAndDate",
        on: {
          VALIDATE_BOOKING: {
            target: "validateBooking",
            actions: ["addBookingValuesAction", "setSelectedCurrencyAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
          BACK: { target: "depots" },
        },
      },
      validateBooking: {
        invoke: {
          id: "validateBooking",
          src: validateBooking,
        },
        on: {
          BOOKING_VALID: [
            {
              target: "persist",
              actions: ["clearValidationErrorAction"],
              cond: (context) => Boolean(context.skipSummaryStep),
            },
            {
              target: "summary",
              actions: ["clearValidationErrorAction"],
            },
          ],
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      summary: {
        on: {
          PERSIST: { target: "persist" },
          BACK: { target: "priceAndDate" },
        },
      },
      persist: {
        invoke: {
          id: "persistBooking",
          src: persistBooking,
        },
        on: {
          BOOKING_VALID: {
            target: "persisted",
          },
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      persisted: {
        type: "final",
      },
      somethingWrong: {
        type: "final",
        id: "somethingWrong",
        entry: ["addAxiosErrorToContextAction"],
      },
    },
  },
  {
    actions: {
      setSymbolAction: assign(setSymbolAction),
      initFromInvestmentTypeAction: assign(initFromInvestmentTypeAction),
      setSelectedCurrencyAction: assign(setSelectedCurrencyAction),
      setEntryQuoteAction: assign(setEntryQuoteAction),
      setSelectableDepotsAction: assign(setSelectableDepotsAction),
      setBookingTypeAction: assign(setBookingTypeAction),
      setNameAndDepotAction: assign(setNameAndDepotAction),
      setDepotAction: assign(setDepotAction),
      addBookingValuesAction: assign(addBookingValuesAction),
      addValidationErrorAction: assign(addValidationErrorAction),
      clearValidationErrorAction: assign(clearValidationErrorAction),
      addAxiosErrorToContextAction: assign(addAxiosErrorToContextAction),
    },
  }
);
